import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import type {
  AllLimitsResponse,
  UpdateCalculatorLimitsRequest,
  UpdateInsightLimitsRequest,
  UpdateConnectionLimitsRequest
} from '../types/limits';

/**
 * Get all limit configurations (admin only)
 * @returns All limit configurations
 */
export const getLimits = async (): Promise<AllLimitsResponse> => {
  try {
    const getLimitsFunction = httpsCallable<void, AllLimitsResponse>(
      functions,
      'getLimits'
    );
    const result = await getLimitsFunction();
    return result.data;
  } catch (error) {
    console.error('Error fetching limits:', error);
    // Rethrow the original error so the Firebase `code` and the server's
    // HttpsError message survive for ErrorService.getUserFriendlyMessage.
    throw error instanceof Error ? error : new Error('Failed to fetch limits. Admin access required.');
  }
};

/**
 * Update calculator limits (admin only)
 * @param updates - Partial updates to calculator limits
 * @returns Success status
 */
export const updateCalculatorLimits = async (
  updates: UpdateCalculatorLimitsRequest
): Promise<{ success: boolean }> => {
  try {
    const updateFunction = httpsCallable<
      UpdateCalculatorLimitsRequest,
      { success: boolean }
    >(functions, 'updateCalculatorLimits');
    const result = await updateFunction(updates);
    return result.data;
  } catch (error) {
    console.error('Error updating calculator limits:', error);
    throw error instanceof Error ? error : new Error('Failed to update calculator limits');
  }
};

/**
 * Update insight limits (admin only)
 * @param updates - Partial updates to insight limits
 * @returns Success status
 */
export const updateInsightLimits = async (
  updates: UpdateInsightLimitsRequest
): Promise<{ success: boolean }> => {
  try {
    const updateFunction = httpsCallable<
      UpdateInsightLimitsRequest,
      { success: boolean }
    >(functions, 'updateInsightLimits');
    const result = await updateFunction(updates);
    return result.data;
  } catch (error) {
    console.error('Error updating insight limits:', error);
    throw error instanceof Error ? error : new Error('Failed to update insight limits');
  }
};

/**
 * Update connection limits (admin only)
 * @param updates - Partial updates to connection limits
 * @returns Success status
 */
export const updateConnectionLimits = async (
  updates: UpdateConnectionLimitsRequest
): Promise<{ success: boolean }> => {
  try {
    const updateFunction = httpsCallable<
      UpdateConnectionLimitsRequest,
      { success: boolean }
    >(functions, 'updateConnectionLimits');
    const result = await updateFunction(updates);
    return result.data;
  } catch (error) {
    console.error('Error updating connection limits:', error);
    throw error instanceof Error ? error : new Error('Failed to update connection limits');
  }
};
